import React from 'react';
import { Smartphone, Apple } from 'lucide-react';

const APP_STORE_URL = 'https://apps.apple.com/fr/app/doctodays/id6763763527';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.doctodays.mobile';

/**
 * App store download badges (Apple App Store + Google Play).
 *
 * Variants:
 *  - "section" (default): big colorful section with title + CTA (homepage)
 *  - "compact": just the 2 buttons side by side, no title (footer)
 *  - "inline": just the 2 buttons, smaller (navbar, banners)
 */
export const AppDownloadBadges = ({ variant = 'section' }) => {
  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap gap-3" data-testid="app-download-badges-compact">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-900 rounded-xl hover:bg-emerald-50 transition-all border border-slate-200 hover:border-emerald-300 hover:scale-[1.02]"
          data-testid="app-store-badge-footer"
          aria-label="Télécharger DoctoDays sur l'App Store"
        >
          <Apple className="w-5 h-5" />
          <div className="text-left leading-tight">
            <div className="text-[10px] uppercase text-slate-500">Télécharger sur</div>
            <div className="text-sm font-semibold">App Store</div>
          </div>
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-900 rounded-xl hover:bg-emerald-50 transition-all border border-slate-200 hover:border-emerald-300 hover:scale-[1.02]"
          data-testid="play-store-badge-footer"
          aria-label="Disponible sur Google Play"
        >
          <Smartphone className="w-5 h-5" />
          <div className="text-left leading-tight">
            <div className="text-[10px] uppercase text-slate-500">Disponible sur</div>
            <div className="text-sm font-semibold">Google Play</div>
          </div>
        </a>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="flex gap-2" data-testid="app-download-badges-inline">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium"
          data-testid="app-store-badge-inline"
        >
          <Apple className="w-4 h-4" /> App Store
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium"
          data-testid="play-store-badge-inline"
        >
          <Smartphone className="w-4 h-4" /> Google Play
        </a>
      </div>
    );
  }

  // Section variant (homepage) — big & centered
  return (
    <section
      className="py-16 md:py-24 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 relative overflow-hidden"
      data-testid="app-download-section"
    >
      {/* Decorative circles */}
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-emerald-500/30 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-emerald-900/30 rounded-full blur-3xl" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left : text */}
          <div className="text-white">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-sm font-medium mb-4">
              📱 Nouveau · L'app DoctoDays est disponible
            </span>
            <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-4">
              Prenez vos rendez-vous<br />directement depuis votre poche
            </h2>
            <p className="text-emerald-50 text-lg mb-8 max-w-lg">
              Téléchargez gratuitement l'application DoctoDays sur iOS et Android.
              Recherche de praticiens, prise de RDV en 3 clics, rappels automatiques
              et itinéraire intégré.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-4 bg-black text-white rounded-2xl hover:bg-slate-900 transition-all hover:scale-[1.03] shadow-xl"
                data-testid="app-store-badge-hero"
                aria-label="Télécharger DoctoDays sur l'App Store"
              >
                <Apple className="w-8 h-8" />
                <div className="text-left leading-tight">
                  <div className="text-xs opacity-80">Télécharger sur</div>
                  <div className="text-lg font-semibold">App Store</div>
                </div>
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-4 bg-black text-white rounded-2xl hover:bg-slate-900 transition-all hover:scale-[1.03] shadow-xl"
                data-testid="play-store-badge-hero"
                aria-label="Disponible sur Google Play"
              >
                <Smartphone className="w-8 h-8" />
                <div className="text-left leading-tight">
                  <div className="text-xs opacity-80">Disponible sur</div>
                  <div className="text-lg font-semibold">Google Play</div>
                </div>
              </a>
            </div>

            <div className="flex items-center gap-6 mt-8 text-emerald-100 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⭐</span>
                <span>100 % gratuit</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🔒</span>
                <span>Données sécurisées</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚡</span>
                <span>Réservation instantanée</span>
              </div>
            </div>
          </div>

          {/* Right : phone mockup made of CSS only */}
          <div className="relative hidden md:flex justify-center" aria-hidden="true">
            <div className="relative">
              {/* Phone frame */}
              <div className="w-72 h-[560px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl">
                <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-slate-900 rounded-b-2xl z-20" />
                  {/* Screen content */}
                  <div className="pt-12 px-5 h-full bg-gradient-to-b from-emerald-50 to-white">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-slate-900">DoctoDays</h3>
                      <p className="text-xs text-slate-500">Trouvez votre praticien</p>
                    </div>
                    {/* Search bar */}
                    <div className="bg-white rounded-2xl shadow-md p-3 mb-4">
                      <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-slate-50 rounded-xl">
                        <span className="text-slate-400">🔍</span>
                        <span className="text-sm text-slate-400">Spécialité</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl">
                        <span className="text-slate-400">📍</span>
                        <span className="text-sm text-slate-400">Ville</span>
                      </div>
                      <div className="mt-3 px-4 py-2.5 bg-emerald-600 text-white text-center rounded-xl text-sm font-semibold">
                        Rechercher
                      </div>
                    </div>
                    {/* Doctor cards */}
                    {[1, 2].map((i) => (
                      <div key={i} className="bg-white rounded-2xl shadow-sm p-3 mb-3 flex gap-3 border border-slate-100">
                        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 font-bold text-sm">
                          Dr
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            Dr. Médecin {i}
                          </div>
                          <div className="text-xs text-slate-500">Généraliste</div>
                          <div className="text-xs text-emerald-600 font-medium mt-1">Dispo aujourd'hui</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadBadges;
